import { cn } from '@hs/ui';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import { TIMER_PROGRESS_STROKE_WIDTH, TIMER_PROGRESS_VIEW_SIZE } from './timerLayout';
const RADIUS = (TIMER_PROGRESS_VIEW_SIZE - TIMER_PROGRESS_STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

type TimerProgressRingProps = {
  value: number;
  className?: string;
};

export type TimerProgressRingHandle = {
  setValue: (value: number) => void;
};

function toStrokeOffset(value: number): number {
  const clamped = Math.min(100, Math.max(0, value));
  return CIRCUMFERENCE - (clamped / 100) * CIRCUMFERENCE;
}

/** 纯 SVG 进度环；支持 ref  imperative 更新，避免 CSS scale 容器内不重绘 */
export const TimerProgressRing = forwardRef<TimerProgressRingHandle, TimerProgressRingProps>(
  function TimerProgressRing({ value, className }, ref) {
    const fillRef = useRef<SVGCircleElement>(null);

    const applyValue = useCallback((next: number) => {
      fillRef.current?.setAttribute('stroke-dashoffset', String(toStrokeOffset(next)));
    }, []);

    useImperativeHandle(ref, () => ({ setValue: applyValue }), [applyValue]);

    useLayoutEffect(() => {
      applyValue(value);
    }, [applyValue, value]);

    return (
      <svg
        aria-label="计时进度"
        role="img"
        viewBox={`0 0 ${TIMER_PROGRESS_VIEW_SIZE} ${TIMER_PROGRESS_VIEW_SIZE}`}
        className={cn('size-full', className)}
      >
        <circle
          cx={TIMER_PROGRESS_VIEW_SIZE / 2}
          cy={TIMER_PROGRESS_VIEW_SIZE / 2}
          r={RADIUS}
          fill="none"
          className="stroke-default-200"
          strokeWidth={TIMER_PROGRESS_STROKE_WIDTH}
        />
        <circle
          ref={fillRef}
          cx={TIMER_PROGRESS_VIEW_SIZE / 2}
          cy={TIMER_PROGRESS_VIEW_SIZE / 2}
          r={RADIUS}
          fill="none"
          className="stroke-red-500"
          strokeWidth={TIMER_PROGRESS_STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={toStrokeOffset(value)}
          transform={`rotate(-90 ${TIMER_PROGRESS_VIEW_SIZE / 2} ${TIMER_PROGRESS_VIEW_SIZE / 2})`}
        />
      </svg>
    );
  },
);
